import React from "react";
import {
	Avatar,
	Box,
	Link as MuiLink,
	Stack,
	Typography
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
	DataGridPro,
	GridColDef,
	GridRenderCellParams
} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {LeaderboardPointsGuideModal} from "@/app2/components/LeaderboardCard";
import {
	formatLeaderboardRank,
	isLeaderboardPersistedRankDataSource
} from "@/app2/components/LeaderboardCard/leaderboardUiUtils";
import {LeaderboardDateFilters} from "./LeaderboardDateFilters";
import {createDefaultLeaderboardDateFilters, type LeaderboardDateFilterState} from "./leaderboardDateRange";
import {
	useListCohortLeaderboard,
	type CohortLeaderboardEntryDto,
	type LeaderboardsReadInternalControllerListCohortLeaderboardParams
} from "@/app2/shared/data";
import {useOwnedCohortId} from "@/app2/shared/hooks";

type AdminLeaderboardGridRow = {
	id: number;
	rank: number;
	isPositiveRank: boolean;
	name: string;
	role: string;
	points: number;
	avatarUrl?: string;
};

const useStyles = makeStyles()((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
		width: "100%"
	},
	headerRow: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		alignItems: "flex-start",
		justifyContent: "space-between",
		columnGap: theme.spacing(3),
		rowGap: theme.spacing(2),
		marginBottom: theme.spacing(1)
	},
	titleBlock: {
		flex: "1 1 280px",
		minWidth: 0
	},
	pageTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(1)
	},
	subtitle: {
		fontWeight: 400,
		fontSize: "16px",
		letterSpacing: "0.15px",
		opacity: 0.8,
		color: theme.palette.text.secondary,
		maxWidth: 720
	},
	pointsLink: {
		cursor: "pointer",
		fontWeight: 600,
		textDecoration: "underline",
		textUnderlineOffset: 3,
		color: theme.palette.text.secondary,
		verticalAlign: "baseline",
		background: "none",
		border: "none",
		padding: 0,
		font: "inherit"
	},
	filtersWrap: {
		flexShrink: 0,
		alignSelf: "flex-start"
	},
	dataGrid: {
		"& .MuiDataGrid-columnHeaderTitle": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		},
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		},
		"& .MuiDataGrid-columnHeader svg path": {
			fill: theme.palette.common.white
		}
	},
	nameCell: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		gap: theme.spacing(1.5),
		minWidth: 0,
		width: "100%",
		overflow: "hidden"
	},
	nameTextStack: {
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		textAlign: "left"
	},
	namePrimary: {
		fontWeight: 700,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		width: "100%"
	},
	nameSecondary: {
		color: theme.palette.text.secondary,
		fontSize: theme.typography.body2.fontSize,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		width: "100%"
	},
	avatar: {
		width: 40,
		height: 40,
		border: `1px solid ${theme.palette.divider}`
	},
	rankCell: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.25),
		height: "100%"
	},
	rankNumber: {
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
		color: theme.palette.text.secondary
	},
	trendIcon: {
		fontSize: "34px",
		lineHeight: 1,
		display: "flex"
	},
	trendUp: {
		color: theme.palette.success.main,
		"& path": {
			fill: theme.palette.success.main
		}
	},
	trendDown: {
		color: theme.palette.error.main,
		"& path": {
			fill: theme.palette.error.main
		}
	},
	statusMessage: {
		padding: theme.spacing(3, 0),
		color: theme.palette.text.secondary
	}
}));

function trimUnknownToString(value: unknown): string {
	if (value == null || value === false) return "";
	if (typeof value === "string") return value.trim();
	return String(value).trim();
}

function cohortEntryIsTrendPositive(entry: CohortLeaderboardEntryDto): boolean {
	/** OpenAPI types `isPositiveRank` loosely; runtime is boolean when `dataSource` is `leaderboard`. */
	const v = entry.isPositiveRank as unknown;
	return v === true || v === "true";
}

function cohortEntryDisplayName(entry: CohortLeaderboardEntryDto): string {
	const first = trimUnknownToString(entry.firstName);
	const last = trimUnknownToString(entry.lastName);
	return [first, last].filter(Boolean).join(" ");
}

function mapCohortEntryToGridRow(entry: CohortLeaderboardEntryDto): AdminLeaderboardGridRow {
	const name = cohortEntryDisplayName(entry);
	const profileImage = trimUnknownToString(entry.profileImage);
	return {
		id: entry.userId,
		rank: entry.rank,
		isPositiveRank: cohortEntryIsTrendPositive(entry),
		name: name || "—",
		role: trimUnknownToString(entry.jobTitle) || "—",
		points: entry.totalPoints,
		avatarUrl: profileImage || undefined
	};
}

/**
 * Cohort list query params. Omit `dateFrom` / `dateTo` for "This month" so the API
 * uses persisted `leaderboard` rows for the current period (default server behavior).
 */
function buildCohortLeaderboardParams(
	filters: LeaderboardDateFilterState,
	page: number,
	pageSize: number
): LeaderboardsReadInternalControllerListCohortLeaderboardParams {
	const base: LeaderboardsReadInternalControllerListCohortLeaderboardParams = {
		page: page + 1,
		pageSize
	};

	if (filters.dateRangeType === "lastMonth") {
		return base;
	}

	return {
		...base,
		dateFrom: filters.dateFrom,
		dateTo: filters.dateTo
	};
}

function initialsFromName(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

function PositiveRankGlyph({
	isPositiveRank,
	trendIcon,
	trendUp,
	trendDown
}: {
	isPositiveRank: boolean;
	trendIcon: string;
	trendUp: string;
	trendDown: string;
}) {
	return isPositiveRank ? (
		<ArrowDropUpIcon className={`${trendIcon} ${trendUp}`} aria-hidden />
	) : (
		<ArrowDropDownIcon className={`${trendIcon} ${trendDown}`} aria-hidden />
	);
}

export const AdminLeaderboardList: React.FC = () => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();
	const cohortId = useOwnedCohortId();
	const [dateFilters, setDateFilters] = React.useState(createDefaultLeaderboardDateFilters);
	const [pointsGuideOpen, setPointsGuideOpen] = React.useState(false);
	const [page, setPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);

	const listParams = React.useMemo(
		() => buildCohortLeaderboardParams(dateFilters, page, pageSize),
		[dateFilters, page, pageSize]
	);

	const {
		data: cohortList,
		isPending,
		isFetching,
		isError
	} = useListCohortLeaderboard(cohortId ?? undefined, listParams, {
		enabled: cohortId != null
	});

	React.useEffect(() => {
		setPage(0);
	}, [dateFilters.dateRangeType, dateFilters.dateFrom, dateFilters.dateTo]);

	const rows = React.useMemo(
		() => (cohortList?.items ?? []).map(mapCohortEntryToGridRow),
		[cohortList?.items]
	);

	const rowCount = cohortList?.totalCount ?? 0;
	const isLoading = cohortId != null && (isPending || isFetching);
	const showRankTrend = isLeaderboardPersistedRankDataSource(cohortList?.dataSource);

	const subtitlePrefix = t(
		"admin_leaderboard_subtitle_prefix",
		"Track employee progress by points earned from lessons, courses, and quizzes."
	);
	const pointsLinkLabel = t(
		"leaderboard_points_guide_link",
		"See how we calculate each point"
	);

	const columns = React.useMemo<GridColDef<AdminLeaderboardGridRow>[]>(
		() => [
			{
				field: "rank",
				headerName: t("leaderboard_modal_col_rank", "Rank"),
				width: 120,
				sortable: false,
				align: "left",
				headerAlign: "left",
				renderCell: (params: GridRenderCellParams<AdminLeaderboardGridRow>) => (
					<Box className={classes.rankCell}>
						<Typography component="span" className={classes.rankNumber}>
							{formatLeaderboardRank(params.row.rank)}
						</Typography>
						{showRankTrend ? (
							<PositiveRankGlyph
								isPositiveRank={params.row.isPositiveRank}
								trendIcon={classes.trendIcon}
								trendUp={classes.trendUp}
								trendDown={classes.trendDown}
							/>
						) : null}
					</Box>
				)
			},
			{
				field: "name",
				headerName: t("leaderboard_modal_col_name", "Name"),
				flex: 1,
				minWidth: 280,
				sortable: false,
				align: "left",
				headerAlign: "left",
				renderCell: (params: GridRenderCellParams<AdminLeaderboardGridRow>) => (
					<Box className={classes.nameCell}>
						<Avatar
							className={classes.avatar}
							src={params.row.avatarUrl}
							alt=""
						>
							{initialsFromName(params.row.name)}
						</Avatar>
						<Stack className={classes.nameTextStack} spacing={0}>
							<Typography variant="body2" className={classes.namePrimary}>
								{params.row.name}
							</Typography>
							<Typography variant="caption" className={classes.nameSecondary}>
								{params.row.role}
							</Typography>
						</Stack>
					</Box>
				)
			},
			{
				field: "points",
				headerName: t("leaderboard_modal_col_points", "Points"),
				width: 120,
				type: "number",
				sortable: false,
				align: "right",
				headerAlign: "right",
				valueFormatter: (params) =>
					typeof params.value === "number" ? params.value.toLocaleString() : ""
			}
		],
		[t, classes, showRankTrend]
	);

	const gridStatus = (() => {
		if (cohortId == null) {
			return (
				<Typography variant="body2" className={classes.statusMessage}>
					{t(
						"admin_leaderboard_no_cohort",
						"No company cohort is available for this account."
					)}
				</Typography>
			);
		}
		if (isError) {
			return (
				<Typography variant="body2" className={classes.statusMessage}>
					{t(
						"admin_leaderboard_load_error",
						"Could not load leaderboard. Try again later."
					)}
				</Typography>
			);
		}
		return null;
	})();

	return (
		<Box className={classes.root} data-test-id="admin-leaderboard-page">
			<Box className={classes.headerRow}>
				<Box className={classes.titleBlock}>
					<Typography variant="h3" component="h1" className={classes.pageTitle}>
						{t("admin_leaderboard_page_title", "Leaderboard")}
					</Typography>
					<Typography variant="body1" component="p" className={classes.subtitle}>
						{subtitlePrefix}{" "}
						<MuiLink
							component="button"
							type="button"
							className={classes.pointsLink}
							onClick={() => setPointsGuideOpen(true)}
							data-test-id="admin-leaderboard-points-guide-link"
						>
							{pointsLinkLabel}
						</MuiLink>
					</Typography>
				</Box>
				<Box className={classes.filtersWrap} data-test-id="admin-leaderboard-filters">
					<LeaderboardDateFilters value={dateFilters} onChange={setDateFilters} />
				</Box>
			</Box>

			{gridStatus ?? (
				<DataGridPro
					className={classes.dataGrid}
					rows={rows}
					columns={columns}
					loading={isLoading}
					disableSelectionOnClick
					disableColumnMenu
					pagination
					paginationMode="server"
					page={page}
					pageSize={pageSize}
					onPageChange={setPage}
					onPageSizeChange={(size) => {
						setPageSize(size);
						setPage(0);
					}}
					rowsPerPageOptions={[25, 50, 100]}
					rowCount={rowCount}
					autoHeight
					getRowId={(row) => row.id}
					data-test-id="admin-leaderboard-grid"
				/>
			)}

			<LeaderboardPointsGuideModal
				open={pointsGuideOpen}
				onClose={() => setPointsGuideOpen(false)}
			/>
		</Box>
	);
};
